// Shared formatting + math helpers for Brand Mode.

// The six deliverable types, in display order. Keys match the NILx rate card
// (statistics.nilRates) on the backend.
export const DELIVERABLES = [
  { key: "socialPost", label: "Social media post" },
  { key: "story", label: "Story" },
  { key: "reel", label: "Reel / TikTok" },
  { key: "longVideo", label: "Long form video" },
  { key: "events", label: "Events" },
  { key: "share", label: "Repost" },
];

export const emptyDeliverables = () =>
  DELIVERABLES.reduce((acc, d) => ({ ...acc, [d.key]: 0 }), {});

// Estimated-cost range convention shared with the backend (nil_value_views
// uses the same ±15% band for cacheValue).
export const RANGE_LOW = 0.85;
export const RANGE_HIGH = 1.15;

export const estCostRange = (nilRates, deliverables) => {
  let total = 0;
  let priced = false;
  DELIVERABLES.forEach(({ key }) => {
    const qty = Number(deliverables?.[key]) || 0;
    const rate = Number(nilRates?.[key]) || 0;
    if (qty > 0 && rate > 0) {
      total += qty * rate;
      priced = true;
    }
  });
  if (!priced) return null;
  return { min: Math.round(total * RANGE_LOW), max: Math.round(total * RANGE_HIGH) };
};

export const deliverableCount = (deliverables) =>
  DELIVERABLES.reduce((sum, d) => sum + (Number(deliverables?.[d.key]) || 0), 0);

// "within" | "below" | "above" against the estimated range, or null.
export const offerStatus = (offer, est) => {
  if (!offer || !est) return null;
  if (offer < est.min) return "below";
  if (offer > est.max) return "above";
  return "within";
};

export const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "";
  return `$${Math.round(Number(n)).toLocaleString()}`;
};

// 287000 -> "287K", 1200000 -> "1.2M", 950 -> "950"
export const fmtCompact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    return `${m >= 10 ? Math.round(m) : Math.round(m * 10) / 10}M`;
  }
  if (v >= 1_000) return `${Math.round(v / 1_000)}K`;
  return `${Math.round(v)}`;
};

export const fmtMoneyCompact = (n) => {
  if (n === null || n === undefined) return "";
  return `$${fmtCompact(n)}`;
};

// {min, max} -> "$120K–$205K" (compact) or "$18,400–$29,900" (full)
export const fmtRange = (range, { compact = true } = {}) => {
  if (!range || (!range.min && !range.max)) return "";
  const fmt = compact ? fmtMoneyCompact : fmtMoney;
  return `${fmt(range.min)}–${fmt(range.max)}`;
};

// Placeholder for a value the product has no data for: an en dash, never an
// em dash (the athlete and coach pages use the same one).
export const EMPTY_VALUE = "\u2013";

export const fullName = (a) => `${a?.name || ""} ${a?.surname || ""}`.trim();

// Badge dot colours (the bm-info / bm-positive / bm-orange / bm-attention /
// bm-critical tokens) for an athlete's archetype and for an offer measured
// against its estimated range. Shared by the Shortlist table and its modals.
export const ARCHETYPE_DOT = {
  "Visibility Star": "#2f6fed",
  "Hidden Gem": "#1f9d55",
  "Complete Athlete": "#FF4400",
};
export const ARCHETYPE_DOT_FALLBACK = "#b8b8bc";

export const OFFER_RANGE = {
  within: { label: "Within range", dot: "#1f9d55" },
  below: { label: "Below range", dot: "#edd200" },
  above: { label: "Above range", dot: "#e2241a" },
};

// Platform display metadata. Chart colors are the validated categorical
// palette (brand orange first, then indigo / green / purple; the near-neutral
// Figma grays fail the colorblind-separation and chroma checks).
export const PLATFORMS = [
  { key: "instagram", label: "Instagram", color: "#FF4400" },
  { key: "tiktok", label: "TikTok", color: "#465A9E" },
  { key: "youtube", label: "YouTube", color: "#0E9068" },
  { key: "x", label: "X", color: "#8D6FB5" },
];

// Strip an enriched shortlist/campaign item back to the payload the API takes.
export const toPayloadItems = (items) =>
  (items || []).map((i) => ({
    athleteId: i.athleteId,
    deliverables: i.deliverables,
    offer: i.offer,
  }));

// "3 of 10, Aaliyah Rogers carries the highest CacheScore (81)..." style
// summary for the Compare banner.
export const compareInsight = (athletes) => {
  if (!athletes || athletes.length < 2) return null;
  const byScore = [...athletes].sort(
    (a, b) => (b.cacheScore || 0) - (a.cacheScore || 0),
  );
  const top = byScore[0];
  const priced = athletes.filter((a) => a.cacheValue?.min);
  let valueLine = "";
  if (priced.length) {
    const cheapest = [...priced].sort(
      (a, b) => (a.cacheValue.min || 0) - (b.cacheValue.min || 0),
    )[0];
    if (cheapest.athleteId !== top.athleteId) {
      valueLine = ` ${fullName(cheapest)} is the lowest entry point at ${fmtRange(
        cheapest.cacheValue,
      )}, the value play if budget is the constraint.`;
    }
  }
  return (
    `Across ${athletes.length} athletes, ${fullName(top)} carries the highest ` +
    `CacheScore (${top.cacheScore}) and the strongest partnership profile.` +
    valueLine
  );
};
